'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { authApi, type UserPublic, type AuthResponse } from '@/lib/api/auth'

interface AuthContextValue {
  user: UserPublic | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  loginViaTelegramCode: (code: string) => Promise<void>
  confirmEmailRegistration: (code: string) => Promise<void>
  startGuestDemo: () => Promise<string>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  // Загрузка текущего пользователя при монтировании
  const refreshUser = useCallback(async () => {
    const accessToken = localStorage.getItem('access_token')
    if (!accessToken) {
      setUser(null)
      setIsLoading(false)
      return
    }

    try {
      const userData = await authApi.me()
      setUser(userData)
    } catch (error) {
      console.error('Failed to fetch user:', error)
      setUser(null)
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Обработка callback с токенами из URL (используем window.location вместо useSearchParams)
  useEffect(() => {
    if (typeof window === 'undefined') return

    const urlParams = new URLSearchParams(window.location.search)
    const accessToken = urlParams.get('access_token')
    const refreshToken = urlParams.get('refresh_token')
    
    if (accessToken && refreshToken) {
      localStorage.setItem('access_token', accessToken)
      localStorage.setItem('refresh_token', refreshToken)
      
      // Очищаем URL параметры
      router.replace('/auth/callback')
      
      // Загружаем данные пользователя
      refreshUser()
    }
  }, [router, refreshUser])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  const login = async (email: string, password: string) => {
    const response: AuthResponse = await authApi.login({ email, password })
    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('refresh_token', response.refresh_token)
    setUser(response.user)
  }

  const startGuestDemo = async (): Promise<string> => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('selected_shop_id')
    localStorage.removeItem('cached_shops')
    localStorage.removeItem('cached_shops_time')
    const response = await authApi.loginGuestDemo()
    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('refresh_token', response.refresh_token)
    localStorage.setItem('selected_shop_id', response.demo_shop_id)
    setUser(response.user)
    return response.demo_shop_id
  }

  const loginViaTelegramCode = async (code: string): Promise<void> => {
    const response: AuthResponse = await authApi.loginViaTelegramCode(code)
    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('refresh_token', response.refresh_token)
    setUser(response.user)
  }

  const confirmEmailRegistration = async (code: string): Promise<void> => {
    const response: AuthResponse = await authApi.confirmEmailRegistration(code)
    localStorage.setItem('access_token', response.access_token)
    localStorage.setItem('refresh_token', response.refresh_token)
    setUser(response.user)
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('selected_shop_id')
      localStorage.removeItem('cached_shops')
      localStorage.removeItem('cached_shops_time')
      setUser(null)
    }
  }

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    loginViaTelegramCode,
    confirmEmailRegistration,
    startGuestDemo,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

